import { trackCoreCollectionsProductBasedCollectionPageProductTileClick } from "@faire/web-api/events/coreCollections/click/productBasedCollectionPageProductTile";
import { trackCoreCollectionsQuickAddAddOptionsClick } from "@faire/web-api/events/coreCollections/click/quickAddAddOptions";
import { trackCoreCollectionsQuickAddClickClick } from "@faire/web-api/events/coreCollections/click/quickAddClick";
import { trackCoreCollectionsQuickAddModalAddToBagButtonClick } from "@faire/web-api/events/coreCollections/click/quickAddModalAddToBagButton";
import { trackCoreCollectionsQuickAddOptionsTrackingClick } from "@faire/web-api/events/coreCollections/click/quickAddOptionsTracking";
import { trackCoreCollectionsQuickAddTrackingClick } from "@faire/web-api/events/coreCollections/click/quickAddTracking";
import { trackCoreCollectionsProductBasedCollectionPageProductTileGirdView } from "@faire/web-api/events/coreCollections/view/productBasedCollectionPageProductTileGird";
import { ICartItemV2 } from "@faire/web-api/faire/carts/ICartItemV2";
import { ICollectionPromo } from "@faire/web-api/indigofair/data/ICollectionPromo";
import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import { IRetailerProductBasedCollectionResponse } from "@faire/web-api/indigofair/data/IRetailerProductBasedCollectionResponse";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { Location } from "history";
import React, { useMemo } from "react";

import { ProductTile } from "@faire/retailer/features/MarketplaceItems/ProductTile";
import {
  QuickAddModalEvent,
  QuickAddModalContext,
  IQuickAddModalContextData,
} from "@faire/retailer/features/ProductQuickAddModal/QuickAddModalContext";
import { ProductPagePaths } from "@faire/retailer/paths/ProductPage";

import { useAddPendingCartItem } from "../../brand/BrandCart/actions/useAddPendingCartItem";
import { BrandCartViewState } from "../../brand/BrandCart/BrandCartViewState";
import { useBrandPageCartStateContext } from "../../brand/contexts/BrandPageCartStateContext";

interface IProductBasedCollectionItemProps {
  productTile: ILeanProductTile;
  collection: IRetailerProductBasedCollectionResponse.IRetailerProductBasedCollection;
  showVerticalImagery: boolean;
  brandCartViewState?: BrandCartViewState;
  collectionPromo?: ICollectionPromo;
}

export const ProductBasedCollectionItem: React.FC<
  IProductBasedCollectionItemProps
> = ({ productTile, collection, showVerticalImagery, collectionPromo }) => {
  const { brandCartViewState } = useBrandPageCartStateContext();
  const addPendingCartItem = useAddPendingCartItem();
  const providerValue: IQuickAddModalContextData = useMemo(
    () => ({
      onEvent: (event, token) => {
        if (event === QuickAddModalEvent.ADD_TO_CART) {
          trackCoreCollectionsQuickAddModalAddToBagButtonClick(
            collection.token!,
            token!,
            collection.request_id ?? ""
          );
        }
      },
    }),
    [collection.token, collection.request_id]
  );

  const trackProductClick = () => {
    trackCoreCollectionsProductBasedCollectionPageProductTileClick(
      collection.token ?? "",
      productTile.product?.token ?? "",
      collection.request_id ?? ""
    );
  };

  const trackQuickAddClick = () => {
    trackCoreCollectionsQuickAddClickClick(
      productTile.product?.token ?? "",
      collection?.token ?? "",
      collectionPromo?.brand_promo_collection_token ?? ""
    );

    trackCoreCollectionsQuickAddTrackingClick(
      productTile.product?.token ?? "",
      collectionPromo?.brand_promo_collection_token ?? ""
    );
  };

  const onQuickAddClick = (value?: number) => {
    trackQuickAddClick();
    addPendingCartItem(
      ICartItemV2.build({
        name: productTile.product?.name,
        image: productTile.images[0],
        product_token: productTile.product?.token,
        product_option_token:
          productTile.quick_add?.quick_add_option?.option_token ?? "",
        quantity: value,
      })
    );
  };

  const trackQuickAddOptionClick = () => {
    trackCoreCollectionsQuickAddAddOptionsClick(
      productTile.product?.token ?? "",
      collection?.token ?? "",
      collectionPromo?.brand_promo_collection_token ?? ""
    );

    trackCoreCollectionsQuickAddOptionsTrackingClick(
      productTile.product?.token ?? "",
      collectionPromo?.brand_promo_collection_token ?? ""
    );
  };

  const trackCollectionProductTileView = () => {
    trackCoreCollectionsProductBasedCollectionPageProductTileGirdView(
      collection?.token ?? "",
      productTile.product?.token ?? "",
      collection.request_id ?? ""
    );
  };

  const generateProductLinkPropsWithCollectionRef = (
    productToken: string,
    _: Location,
    brandToken?: string,
    productOptionToken?: string
  ) => {
    const basePath = ProductPagePaths.index(productToken, {
      brandToken,
      productOptionToken: productOptionToken,
      collectionRef: collection?.token,
    });
    return { productUrl: basePath };
  };

  const viewRef = useDoActionWhenVisible(trackCollectionProductTileView);

  return (
    <QuickAddModalContext.Provider value={providerValue}>
      <div ref={viewRef}>
        <ProductTile
          data-test-id="collection-product-tile"
          key={`${productTile.product?.token}-product-tile`}
          productTile={productTile}
          showVerticalImagery={showVerticalImagery}
          onProductClick={trackProductClick}
          onQuickAddClick={onQuickAddClick}
          onChooseOption={trackQuickAddOptionClick}
          brandName={collection.brand_name}
          updateCartFn={brandCartViewState.updateItems}
          generateProductLinkProps={generateProductLinkPropsWithCollectionRef}
          showEncapsulatedQuickAdd
          recordProductTileImpressionForAds
        />
      </div>
    </QuickAddModalContext.Provider>
  );
};
